import {
  HeartHandshake,
  Brain,
  Moon,
  Pill,
  UtensilsCrossed,
  Sparkles,
} from "lucide-react";

const IMG_CARE =
  "https://images.unsplash.com/photo-1762955911235-adeb8838dcf1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHw0fHxlbGRlcmx5JTIwY2FyZSUyMGhvbWUlMjB3YXJtfGVufDB8fHx8MTc3Njg0MzIwMnww&ixlib=rb-4.1.0&q=85";
const IMG_MEALS =
  "https://images.unsplash.com/photo-1761582861462-ee72cf21336e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwzfHxob21lJTIwY29va2VkJTIwbWVhbCUyMGhlYWx0aHl8ZW58MHx8fHwxNzc2ODQzMjAyfDA&ixlib=rb-4.1.0&q=85";
const IMG_COMMUNITY =
  "https://images.unsplash.com/photo-1773227060446-93239a553f1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwyfHxlbGRlcmx5JTIwY2FyZSUyMGhvbWUlMjB3YXJtfGVufDB8fHx8MTc3Njg0MzIwMnww&ixlib=rb-4.1.0&q=85";

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-32 bg-[#F4F1EA]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mb-14 md:mb-20">
          <p className="reveal text-xs uppercase tracking-[0.3em] font-semibold text-[#667B68]">
            Our Services
          </p>
          <h2
            data-testid="services-title"
            className="reveal font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#1C2A24] mt-4"
            style={{ transitionDelay: "80ms" }}
          >
            Specialized care,
            <br />
            woven into everyday life.
          </h2>
          <p
            className="reveal mt-6 text-[17px] leading-relaxed text-[#3E574B] max-w-2xl"
            style={{ transitionDelay: "140ms" }}
          >
            From morning medication to the last blanket tucked in at night, our
            care is quiet, consistent, and tailored to each resident&rsquo;s
            needs.
          </p>
        </div>

        {/* Bento grid */}
        <div
          className="grid grid-cols-1 md:grid-cols-6 auto-rows-[minmax(180px,auto)] gap-6 lg:gap-8"
          data-testid="services-bento"
        >
          {/* Big feature card — Alzheimer's */}
          <div
            className="reveal md:col-span-4 md:row-span-2 relative overflow-hidden rounded-3xl bg-[#2C4C3B] text-white p-10 md:p-14 flex flex-col justify-between min-h-[380px] group"
            data-testid="service-alzheimers"
          >
            <img
              src={IMG_COMMUNITY}
              alt="Residents enjoying a common room"
              className="absolute inset-0 w-full h-full object-cover opacity-30 group-hover:opacity-40 transition-opacity duration-700 group-hover:scale-[1.03]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-br from-[#1f372a]/80 via-[#2C4C3B]/70 to-[#2C4C3B]/50" />
            <div className="relative">
              <Brain className="w-8 h-8 mb-6" />
              <h3 className="font-heading text-3xl sm:text-4xl lg:text-5xl leading-tight">
                Alzheimer&rsquo;s &amp; Dementia Care
              </h3>
              <p className="mt-5 text-[#E5E1D8] leading-relaxed max-w-xl">
                Predictable routines, calm surroundings, and caregivers trained
                to meet memory loss with patience — not correction. We focus on
                what a resident can still enjoy, every single day.
              </p>
            </div>
            <div className="relative flex items-center gap-3 text-[#F4F1EA]/90 text-sm uppercase tracking-[0.25em]">
              <span className="w-8 h-px bg-[#F4F1EA]/60" />
              Our specialty
            </div>
          </div>

          {/* Adult Foster Care */}
          <div
            className="reveal md:col-span-2 bg-white border border-[#E5E1D8] rounded-3xl p-8 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(44,76,59,0.2)] transition-all duration-300 flex flex-col"
            data-testid="service-foster"
            style={{ transitionDelay: "80ms" }}
          >
            <HeartHandshake className="w-7 h-7 text-[#2C4C3B]" />
            <h3 className="font-heading text-2xl mt-5 text-[#1C2A24]">
              Adult Foster Care
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[#3E574B]">
              A true home for up to five residents — personal, private, and
              genuinely shared.
            </p>
          </div>

          {/* Hospice */}
          <div
            className="reveal md:col-span-2 bg-[#A67C52] text-white rounded-3xl p-8 hover:-translate-y-1 transition-all duration-300 flex flex-col"
            data-testid="service-hospice"
            style={{ transitionDelay: "160ms" }}
          >
            <Moon className="w-7 h-7" />
            <h3 className="font-heading text-2xl mt-5">Hospice &amp; End-of-Life</h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[#F4F1EA]/95">
              Quiet, dignified end-of-life care — alongside your hospice team or
              ours, in a setting that feels like home.
            </p>
          </div>

          {/* Medication + image */}
          <div
            className="reveal md:col-span-2 rounded-3xl overflow-hidden relative min-h-[240px] group"
            data-testid="service-medication"
            style={{ transitionDelay: "80ms" }}
          >
            <img
              src={IMG_CARE}
              alt="Resident receiving personal care"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="absolute inset-0 p-7 flex flex-col justify-end text-white">
              <Pill className="w-6 h-6 mb-3" />
              <h3 className="font-heading text-2xl">Medication Management</h3>
              <p className="text-sm text-[#E5E1D8] mt-2 leading-relaxed">
                Every dose, on time — tracked, documented, and coordinated with
                your physician.
              </p>
            </div>
          </div>

          {/* Meals */}
          <div
            className="reveal md:col-span-2 rounded-3xl overflow-hidden relative min-h-[240px] group"
            data-testid="service-meals"
            style={{ transitionDelay: "160ms" }}
          >
            <img
              src={IMG_MEALS}
              alt="Home-cooked meal"
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
            <div className="absolute inset-0 p-7 flex flex-col justify-end text-white">
              <UtensilsCrossed className="w-6 h-6 mb-3" />
              <h3 className="font-heading text-2xl">Home-Cooked Meals</h3>
              <p className="text-sm text-[#E5E1D8] mt-2 leading-relaxed">
                Real meals at a real table — prepared fresh, with preferences
                and diets in mind.
              </p>
            </div>
          </div>

          {/* Daily Activities */}
          <div
            className="reveal md:col-span-2 bg-white border border-[#E5E1D8] rounded-3xl p-8 hover:-translate-y-1 hover:shadow-[0_20px_40px_-20px_rgba(44,76,59,0.2)] transition-all duration-300 flex flex-col"
            data-testid="service-activities"
            style={{ transitionDelay: "240ms" }}
          >
            <Sparkles className="w-7 h-7 text-[#A67C52]" />
            <h3 className="font-heading text-2xl mt-5 text-[#1C2A24]">
              Daily Activities &amp; 24-Hr Care
            </h3>
            <p className="mt-3 text-[15px] leading-relaxed text-[#3E574B]">
              Gentle routines, music, crafts, and conversation — woven into
              every day with care.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
